import React, { useEffect, useRef, useCallback, memo } from 'react';
import { useForm, useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import {
  FORM_VALUES,
  MORTGAGE_CATEGORIES,
  PRICE_MAX_LENGTH
} from '../../../../../utils/helpers/constants';
import { RentalType } from '../../components/RentalType';
import { FormBlockWrapper } from '../FormBlockWrapper';
import { FormTextInput } from '../../components/FormTextInput';
import { FormNumberSelect } from '../../../components/FormNumberSelect';
import { Checkbox } from '../../../../ui/Checkbox';
import {
  CheckBoxesContainer,
  CheckboxesRow,
  CheckBoxWrapper,
  MortgageFieldWrapper,
  PricePlaceholder,
  RemainTermContainer,
  RemainTermLabel,
  RemainTermRow
} from './styled';
import { PricePerAcre } from '../../components/PricePerAcre';
import { checkIsAreaCategory } from '../../../../../utils/helpers/common';
import { getPriceTranslationKey } from '../../../../../utils/helpers/getPriceTranslationKey';
import { getSymbolFromCurrency } from '../../../../../utils/helpers/getSymbolFromCurrency';
import { clearMortgageFields } from '../../../../../utils/helpers/mortgageForm';
import { Switch } from '../../../../ui/Switch';

interface PricingProps {
  isResidence?: boolean;
  isEdit?: boolean;
}

const PricingComponent: React.FC<PricingProps> = ({
  isResidence = false,
  isEdit = false
}) => {
  const { t } = useTranslation();
  const { change } = useForm();

  const { input: categoriesIdField } = useField(FORM_VALUES.categoryId, {
    subscription: { value: true }
  });
  const { input: leasedField } = useField(FORM_VALUES.leased, {
    subscription: { value: true }
  });
  const { input: areaField } = useField(FORM_VALUES.area, {
    subscription: { value: true }
  });
  const { input: priceField } = useField(FORM_VALUES.price, {
    subscription: { value: true }
  });
  const { input: itemIdField } = useField('itemId', {
    subscription: { value: true }
  });
  const { input: hasBillOfSaleField } = useField(FORM_VALUES.hasBillOfSale, {
    subscription: { value: true }
  });
  const { input: hasMortgageField } = useField(FORM_VALUES.hasMortgage, {
    subscription: { value: true }
  });
  const { input: hasNewMortgageField } = useField(FORM_VALUES.hasNewMortgage, {
    subscription: { value: true }
  });
  const { input: remainYearsField } = useField(FORM_VALUES.remainYears, {
    subscription: { value: true }
  });
  const { input: remainMonthsField } = useField(FORM_VALUES.remainMonths, {
    subscription: { value: true }
  });
  const leased = leasedField.value?.toString() === 'true';
  const categoryId = categoriesIdField.value;
  const hasBillOfSale = hasBillOfSaleField.value;
  const hasMortgage = hasMortgageField.value;
  const hasNewMortgage = hasNewMortgageField.value;
  const remainYears =
    remainYearsField.value !== '' && remainYearsField.value !== undefined
      ? Number(remainYearsField.value)
      : null;
  const remainMonths =
    remainMonthsField.value !== '' && remainMonthsField.value !== undefined
      ? Number(remainMonthsField.value)
      : null;

  const savedHasMortgage = useRef<boolean | null>(null);

  const clearMortgageDetails = useCallback(() => {
    clearMortgageFields(change);
  }, [change]);

  useEffect(() => {
    if (!itemIdField.value) {
      if (leased) {
        change(FORM_VALUES.hasBillOfSale, undefined);
        change(FORM_VALUES.hasMortgage, undefined);
        clearMortgageDetails();
      } else {
        change(FORM_VALUES.hasBillOfSale, false);
        change(FORM_VALUES.hasMortgage, false);
      }
    }
  }, [leased, itemIdField.value, change, clearMortgageDetails]);

  useEffect(() => {
    if (
      !itemIdField.value &&
      hasNewMortgage &&
      !MORTGAGE_CATEGORIES.includes(categoryId)
    ) {
      clearMortgageDetails();
    }
  }, [categoryId, hasNewMortgage, itemIdField.value, clearMortgageDetails]);

  const onCheckboxClick = (field: string, newValue: boolean) => {
    change(field, newValue);
  };

  const handleNewMortgageToggle = (checked: boolean) => {
    if (checked) {
      savedHasMortgage.current = hasMortgage;
      change(FORM_VALUES.hasMortgage, true);
    } else {
      change(FORM_VALUES.hasMortgage, savedHasMortgage.current ?? false);
      savedHasMortgage.current = null;
    }
    change(FORM_VALUES.hasNewMortgage, checked);
  };

  const pricePlaceholder = (
    <PricePlaceholder>
      {t('new_ad.price_placeholder', {
        label: t(getPriceTranslationKey(categoryId, leased)),
        currency: getSymbolFromCurrency('AZN'),
        required: ' *'
      })}
    </PricePlaceholder>
  );

  const isArea = checkIsAreaCategory(categoryId);

  return (
    <FormBlockWrapper header={t('new_ad.price_conditions')} required>
      {leased && <RentalType />}

      {!leased &&
        !isResidence &&
        !isEdit &&
        MORTGAGE_CATEGORIES.includes(categoryId) && (
          <CheckBoxWrapper data-cy="new-mortgage-container">
            <Switch
              id="new_mortgage"
              cypress="new-mortgage-button"
              onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleNewMortgageToggle(e.target.checked)
              }
              name={FORM_VALUES.hasNewMortgage}
              isChecked={hasNewMortgage}
              label={t('new_ad.currently_in_mortgage')}
              stat="new-mortgage-switch"
            />
          </CheckBoxWrapper>
        )}

      {hasNewMortgage ? (
        <>
          <FormTextInput
            inputMode="numeric"
            name={FORM_VALUES.firstPayment}
            placeholder={t('new_ad.first_payment')}
            isEnabledAutoSelectionRange={false}
            maxLength={PRICE_MAX_LENGTH}
            hasErrorField
          />
          <CheckBoxesContainer>
            <MortgageFieldWrapper>
              <FormTextInput
                inputMode="numeric"
                name={FORM_VALUES.monthlyPayment}
                placeholder={t('new_ad.monthly_payment')}
                isEnabledAutoSelectionRange={false}
                maxLength={PRICE_MAX_LENGTH}
                hasErrorField
              />
            </MortgageFieldWrapper>
            <MortgageFieldWrapper>
              <FormTextInput
                inputMode="numeric"
                name={FORM_VALUES.interestRate}
                placeholder={t('new_ad.interest_rate')}
                isEnabledAutoSelectionRange={false}
                maxLength="5"
                hasErrorField
                isFloat
              />
            </MortgageFieldWrapper>
          </CheckBoxesContainer>
          <RemainTermContainer>
            <RemainTermLabel>{t('new_ad.remain_term_label')}</RemainTermLabel>
            <RemainTermRow>
              <MortgageFieldWrapper>
                <FormNumberSelect
                  name={FORM_VALUES.remainYears}
                  max={30}
                  placeholder={t('new_ad.remain_years')}
                  title={t('new_ad.remain_term_years')}
                  disabledZero={remainMonths === 0}
                  cypress="remain-years"
                />
              </MortgageFieldWrapper>
              <MortgageFieldWrapper>
                <FormNumberSelect
                  name={FORM_VALUES.remainMonths}
                  max={11}
                  placeholder={t('new_ad.remain_months')}
                  title={t('new_ad.remain_term_months')}
                  disabledZero={remainYears === 0}
                  cypress="remain-months"
                />
              </MortgageFieldWrapper>
            </RemainTermRow>
          </RemainTermContainer>
        </>
      ) : (
        <FormTextInput
          inputMode="numeric"
          name={FORM_VALUES.price}
          placeholder={pricePlaceholder}
          isEnabledAutoSelectionRange={false}
          maxLength={PRICE_MAX_LENGTH}
          hasErrorField
        />
      )}

      {isArea && !leased && !hasNewMortgage && (
        <PricePerAcre area={areaField.value} price={priceField.value} />
      )}

      {!leased && (
        <CheckboxesRow>
          <CheckBoxWrapper data-cy="bill-of-sale">
            <Checkbox
              id="sale"
              cypress="bill-of-sale-button"
              onChangeHandler={() =>
                onCheckboxClick(FORM_VALUES.hasBillOfSale, !hasBillOfSale)
              }
              name={FORM_VALUES.hasBillOfSale}
              isChecked={hasBillOfSale}
              label={t('search.form.has_bill_of_sale')}
            />
          </CheckBoxWrapper>
          {!(isEdit && hasNewMortgage) && (
            <CheckBoxWrapper data-cy="mortgage" $disabled={hasNewMortgage}>
              <Checkbox
                id="mortgage"
                cypress="mortgage-button"
                onChangeHandler={() =>
                  onCheckboxClick(FORM_VALUES.hasMortgage, !hasMortgage)
                }
                name={FORM_VALUES.hasMortgage}
                isChecked={hasMortgage || hasNewMortgage}
                label={t('search.form.has_mortgage')}
              />
            </CheckBoxWrapper>
          )}
        </CheckboxesRow>
      )}
    </FormBlockWrapper>
  );
};

export const Pricing = memo(PricingComponent);
